/**
 * Shadow-DOM traversal + re-resolution helpers.
 *
 * CSS selectors cannot pierce shadow boundaries: `document.querySelector` never
 * matches an element that lives inside a shadow root. So a selector that needs
 * to reach into shadow DOM can't be a single flat string — it has to be a
 * sequence of per-tree selectors, one per shadow tree, each resolvable within
 * its own root. We join those segments with `SHADOW_BOUNDARY_DELIMITER` and
 * re-resolve them with `resolveSelector`, descending one `shadowRoot` per
 * boundary.
 *
 * This module owns the primitives that knowledge lives in:
 *
 *   - `composedParent` / `rootOf` — walk *out* of a shadow tree (the light-DOM
 *     `parentElement` chain dead-ends at a shadow root; `getRootNode().host`
 *     crosses the boundary).
 *   - `segmentWalk` — split the outward walk into per-tree segments, honoring
 *     the configured max shadow depth.
 *   - `resolveSelector` — the symmetric inverse of the engine's output: split
 *     on the delimiter and pierce open shadow roots to re-find the element.
 *   - `walkComposedAncestors` — yield ancestors up the composed tree, honoring a
 *     shadow-boundary crossing budget (shared by capture and selector layers).
 *   - `collectOpenShadowRoots` — DFS collection of open shadow roots for
 *     observer fan-out and deep `querySelectorAll`.
 *   - `positionalStep` — a shadow-aware `tag:nth-of-type(n)` builder shared by
 *     the orchestrator's descent and the fallback walker, so a shadow-root-top
 *     element is still disambiguated against its `ShadowRoot` siblings.
 *
 * See the design doc:
 *   packages/plugin-autocapture-browser/element-selector-strategy-v1-no-classes.md
 */

/**
 * Delimiter that separates per-shadow-tree segments in a generated selector.
 *
 * Chosen to be visually distinct and trivially splittable, and to be invalid as
 * a real CSS combinator so it can never be confused with a `>` child combinator
 * the engine emits inside a single tree. Example output:
 *
 *   `div#host >>> button:nth-of-type(1)`
 *
 * which `resolveSelector` re-resolves as
 *   `document.querySelector('div#host').shadowRoot.querySelector('button:nth-of-type(1)')`.
 */
export const SHADOW_BOUNDARY_DELIMITER = ' >>> ';

/**
 * Marker prefix for a "direct child from the root" chain inside a shadow tree.
 *
 * A positional chain scoped to a `ShadowRoot` (e.g. `div:nth-of-type(2)`) is not
 * anchored: `querySelector` matches it tree-wide, and `:scope` matches nothing
 * on a `ShadowRoot` (it isn't an element), so there is no CSS way to express
 * "direct child of the root." The generator therefore prefixes such chains with
 * this marker and `resolveSelector` resolves them by strict direct-child descent
 * instead of `querySelector`. Keying off this explicit marker — rather than
 * sniffing a segment for the absence of `#`/`[` — keeps the round-trip correct
 * no matter what the steps contain (tag, `:nth-of-type`, classes, …).
 *
 * `:scope > ` is deliberately valid CSS syntax so a generated selector stays a
 * syntactically legal string; its meaning here is purely the descent signal.
 */
export const SHADOW_CHILD_CHAIN_PREFIX = ':scope > ';

/**
 * Narrowing guard for shadow roots. Tries `instanceof` first, then falls back
 * to a structural check so shadow roots from another realm (e.g. a same-origin
 * iframe, as in a replay mirror) are recognized too: there the calling page's
 * `ShadowRoot` constructor does not match. Safe in environments without a
 * `ShadowRoot` global, and `null`/`undefined` return `false`.
 */
export function isShadowRoot(node: Node): node is ShadowRoot {
  if (typeof ShadowRoot !== 'undefined' && node instanceof ShadowRoot) {
    return true;
  }
  return (
    node != null &&
    node.nodeType === 11 /* DOCUMENT_FRAGMENT_NODE */ &&
    (node as ShadowRoot).host?.nodeType === 1 /* ELEMENT_NODE */
  );
}

/**
 * Anchor a per-tree segment so it re-resolves within a `ShadowRoot`.
 *
 * Both selector algorithms (the strategy-chain fallback and the legacy Chromium
 * `cssPath`) can emit a segment that is only *positionally* specified — a chain
 * of `tag:nth-*` / class steps with no id or attribute to pin it. In the light
 * DOM the chain is implicitly anchored by the unique `<html>` root; inside a
 * shadow root there is no such root element, so `querySelector` matches the
 * chain tree-wide and can resolve to the wrong element (or none).
 *
 * When `segment` is such a root-anchored positional chain inside a shadow root,
 * we prefix it with {@link SHADOW_CHILD_CHAIN_PREFIX} so `resolveSelector`
 * descends by strict direct-child matching instead of `querySelector`. An
 * id/attribute-anchored segment is already resolvable tree-wide and is returned
 * untouched. `isRootAnchored` is the caller's decision (each generator knows
 * whether its own output reached the tree top vs. short-circuited on an id), so
 * this stays correct regardless of what the steps contain.
 */
export function anchorSegmentToShadowScope(segment: string, scope: ParentNode, isRootAnchored: boolean): string {
  if (segment && isRootAnchored && isShadowRoot(scope as Node)) {
    return `${SHADOW_CHILD_CHAIN_PREFIX}${segment}`;
  }
  return segment;
}

/**
 * Whether the legacy `cssPath` output for `el` is a root-anchored positional
 * chain rather than one anchored by an id.
 *
 * `legacyCssPath` short-circuits at the first element — inclusive of `el` —
 * that carries an `id`, emitting `tag#id > …` from there. If some element in
 * `el`'s ancestor chain has an id the emitted selector is id-anchored (a plain,
 * tree-wide-resolvable `querySelector`); if none does, the chain spans the tree
 * top down to `el` via positional steps and must be marked for direct-child
 * descent inside a shadow root. This walks the same `parentElement` chain the
 * legacy walker does, so the two stay in lockstep.
 */
export function legacyPathIsRootAnchored(el: Element): boolean {
  for (let node: Element | null = el; node !== null; node = node.parentElement) {
    if (node.getAttribute('id')) {
      return false;
    }
  }
  return true;
}

/**
 * The queryable root for `el`'s own tree: the enclosing `ShadowRoot` when `el`
 * lives inside shadow DOM, otherwise the owner document. Both support
 * `querySelectorAll`, so uniqueness checks can be scoped to the correct tree.
 *
 * Preserves the legacy default for non-shadow elements (`ownerDocument`), so
 * nothing changes for the common flat-document case.
 */
export function rootOf(el: Element): ParentNode {
  const root = el.getRootNode();
  if (isShadowRoot(root)) {
    return root;
  }
  return el.ownerDocument ?? document;
}

/**
 * The next element up, crossing shadow boundaries: `parentElement` within a
 * tree, or the shadow host when `el` is at the top of a shadow tree. Returns
 * `null` only at the document root (or a detached non-shadow root).
 */
export function composedParent(el: Element): Element | null {
  if (el.parentElement) {
    return el.parentElement;
  }
  const root = el.getRootNode();
  if (isShadowRoot(root)) {
    return root.host;
  }
  return null;
}

/** One tree's contribution to a piercing selector. */
export interface TreeSegment {
  /** Queryable root for this tree — a `Document` or `ShadowRoot`. */
  root: ParentNode;
  /** Element to locate within `root`: the host of the next inner tree, or the original target. */
  target: Element;
}

export interface SegmentWalkResult {
  /** Tree segments, outermost (document) first — the order they're joined for output. */
  segments: TreeSegment[];
  /**
   * True when the target sat deeper than `maxShadowDepth` allowed, so inner
   * trees were dropped and the innermost emitted segment targets the shadow
   * host at the depth limit rather than the original target.
   */
  truncated: boolean;
}

/**
 * Defensive bound on composed-ancestor walks (host-chain iteration in
 * `walkComposedAncestors` and `segmentWalk`). Far above any real shadow nesting
 * (the resolver caps effective depth at `MAX_SHADOW_DOM_DEPTH`). See also
 * {@link MAX_SHADOW_DOM_TRAVERSAL_NODES} for the DFS collection cap.
 */
export const MAX_SHADOW_COMPOSED_WALK_ITERATIONS = 1024;

/**
 * Defensive bound on node visits when collecting open shadow roots via DFS.
 * Caps synchronous work inside MutationObserver callbacks on pathologically
 * large trees. See also {@link MAX_SHADOW_COMPOSED_WALK_ITERATIONS} for the
 * ancestor-walk cap.
 */
export const MAX_SHADOW_DOM_TRAVERSAL_NODES = 50000;

export interface OpenShadowRootEntry {
  root: ShadowRoot;
  /** 1-based shadow-boundary depth relative to the collection `root`. */
  depth: number;
}

/**
 * Yield `start` and each ancestor up the composed tree. Within a tree the walk
 * follows `parentElement`; at the top of a shadow tree it crosses to the host
 * when `crossings < maxShadowDepth`. Yields every element including detached
 * nodes at the top of their tree (where `parentElement` is null).
 */
export function* walkComposedAncestors(start: Element, maxShadowDepth: number): Generator<Element> {
  let node: Element | null = start;
  let crossings = 0;
  let iterations = 0;

  while (node !== null && iterations < MAX_SHADOW_COMPOSED_WALK_ITERATIONS) {
    iterations += 1;
    yield node;

    if (node.parentElement) {
      node = node.parentElement;
      continue;
    }
    if (crossings < maxShadowDepth && typeof node.getRootNode === 'function') {
      const host = composedParent(node);
      if (host && host !== node) {
        crossings += 1;
        node = host;
        continue;
      }
    }
    break;
  }
}

/**
 * Collect the OPEN shadow roots within `root`'s composed subtree (its own light
 * children and any nested shadow trees), each paired with how many shadow
 * boundary crossings deep it sits relative to `root` (1-based). Bounded by
 * `maxShadowDepth` crossings and by {@link MAX_SHADOW_DOM_TRAVERSAL_NODES}
 * total nodes. Closed roots are invisible (`el.shadowRoot` is null) and skipped.
 */
export function collectOpenShadowRoots(root: Element, maxShadowDepth: number): OpenShadowRootEntry[] {
  const found: OpenShadowRootEntry[] = [];
  if (maxShadowDepth <= 0) {
    return found;
  }
  const stack: Array<[Element, number]> = [[root, 0]];
  let visited = 0;
  while (stack.length && visited < MAX_SHADOW_DOM_TRAVERSAL_NODES) {
    visited += 1;
    const [el, depth] = stack.pop() as [Element, number];
    const shadowRoot = el.shadowRoot;
    if (shadowRoot && depth + 1 <= maxShadowDepth) {
      found.push({ root: shadowRoot, depth: depth + 1 });
      const shadowChildren = shadowRoot.children;
      for (let i = 0; i < shadowChildren.length; i++) {
        stack.push([shadowChildren[i], depth + 1]);
      }
    }
    const children = el.children;
    for (let i = 0; i < children.length; i++) {
      stack.push([children[i], depth]);
    }
  }
  return found;
}

/**
 * Split the outward composed walk from `el` into per-tree segments.
 *
 * Always walks fully out to the document so the outermost segment is
 * document-rooted and therefore re-resolvable. `maxShadowDepth` caps how many
 * shadow boundaries the *emitted* selector descends through: when the target is
 * nested deeper than the budget, the innermost (over-budget) trees are dropped
 * and the result is flagged `truncated` — the innermost kept segment then
 * targets the shadow host at the depth limit (a best-effort, still-resolvable
 * selector for an ancestor of the true target).
 */
export function segmentWalk(el: Element, maxShadowDepth: number): SegmentWalkResult {
  // Build innermost-first: one segment per tree, each `target` being the
  // element to locate within that tree (the inner tree's host, or `el`).
  const innermostFirst: TreeSegment[] = [];
  let node: Element | null = el;
  let iterations = 0;

  while (node !== null && iterations < MAX_SHADOW_COMPOSED_WALK_ITERATIONS) {
    iterations += 1;
    const root = rootOf(node);
    innermostFirst.push({ root, target: node });
    const actualRoot = node.getRootNode();
    if (!isShadowRoot(actualRoot)) {
      break; // reached the document (or a detached non-shadow root)
    }
    node = actualRoot.host; // cross the boundary outward
  }

  const totalCrossings = innermostFirst.length - 1;
  let truncated = false;
  let kept = innermostFirst;

  if (totalCrossings > maxShadowDepth) {
    // Drop the innermost over-budget trees, keeping the outermost
    // (maxShadowDepth + 1) trees. The new innermost kept segment's `target` is
    // already the shadow host of the first dropped tree.
    kept = innermostFirst.slice(totalCrossings - maxShadowDepth);
    truncated = true;
  }

  // Reverse to outermost-first so the engine joins document → target order.
  return { segments: kept.reverse(), truncated };
}

/**
 * Re-resolve a generated selector back to its element, piercing open shadow
 * roots at each boundary. The symmetric inverse of `engine.generate`.
 *
 * Returns `null` when any segment fails to match, or when a boundary crossing
 * hits a *closed* shadow root (`host.shadowRoot === null`) — closed roots are
 * opaque from outside, so such selectors cannot round-trip (a documented
 * limitation). Never throws on a malformed segment.
 *
 * @param root      Root to resolve against — almost always `document`.
 * @param selector  A selector produced by the engine (may contain boundary delimiters).
 */
export function resolveSelector(root: ParentNode, selector: string): Element | null {
  const segments = selector.split(SHADOW_BOUNDARY_DELIMITER);
  let scope: ParentNode | null = root;
  let found: Element | null = null;

  for (let i = 0; i < segments.length; i++) {
    if (scope === null) {
      return null; // previous segment's host had no (open) shadow root
    }
    const segment = segments[i].trim();
    try {
      found = resolveSegment(scope, segment);
    } catch {
      return null; // malformed segment — never throw out of the resolver
    }
    if (found === null) {
      return null;
    }
    if (i < segments.length - 1) {
      // Descend into this host's shadow root for the next segment. `null` for a
      // closed root ends resolution gracefully on the next iteration.
      scope = found.shadowRoot;
    }
  }

  return found;
}

/**
 * Resolve a single (non-delimited) segment within `scope`.
 *
 * A segment marked with {@link SHADOW_CHILD_CHAIN_PREFIX} is a root-anchored
 * "direct child from the root" chain (emitted by the fallback for a shadow-root
 * scope, where `querySelector` can't express that anchoring). We strip the
 * marker and resolve by strict direct-child descent. The marker is set
 * explicitly by the generator, so this stays correct regardless of what the
 * steps contain (tag, `:nth-of-type`, classes, …). Every other segment —
 * id/attribute-anchored, or any document-scope segment — resolves via a plain
 * `querySelector`.
 */
function resolveSegment(scope: ParentNode, segment: string): Element | null {
  if (segment.startsWith(SHADOW_CHILD_CHAIN_PREFIX)) {
    return resolveChildChain(scope, segment.slice(SHADOW_CHILD_CHAIN_PREFIX.length));
  }
  return scope.querySelector(segment);
}

/**
 * Resolve a `>`-joined chain as a strict direct-child descent from `scope`. At
 * each level exactly one child matches the step (positional or otherwise via
 * `matches()`), so the walk is deterministic. Returns `null` if any step matches
 * no child.
 */
function resolveChildChain(scope: ParentNode, chain: string): Element | null {
  const steps = chain.split('>').map((s) => s.trim());
  let children: Element[] = Array.from(scope.children);
  let el: Element | null = null;
  for (const step of steps) {
    el = children.find((child) => child.matches(step)) ?? null;
    if (el === null) {
      return null;
    }
    children = Array.from(el.children);
  }
  return el;
}

/**
 * Build a single positional step for `el`: `tag:nth-of-type(n)`, indexed among
 * its same-tag siblings. A parentless element emits the bare tag.
 *
 * `shadowAware` extends the parentless case: an element at the top of a shadow
 * tree has a null `parentElement` but can still have same-tag peers among the
 * `ShadowRoot`'s children, so it is indexed against those instead. When
 * `shadowAware` is false every parentless element emits the bare tag.
 */
export function positionalStep(el: Element, shadowAware: boolean): string {
  const tag = el.tagName.toLowerCase();
  const parent = el.parentElement;

  if (parent === null) {
    // Parentless means the document root (`<html>`), a detached element, or the
    // top of a shadow tree. Only the last has siblings worth indexing, and only
    // when the caller asks for shadow-aware stepping.
    const root = el.getRootNode();
    if (shadowAware && isShadowRoot(root)) {
      return `${tag}:nth-of-type(${sameTypeIndexAmong(el, root.children)})`;
    }
    return tag;
  }

  return `${tag}:nth-of-type(${sameTypeIndexAmong(el, parent.children)})`;
}

/**
 * 1-based index of `el` among same-tag siblings in `siblings`, mirroring
 * `:nth-of-type`. Takes an `HTMLCollection` rather than a parent `Element` so a
 * `ShadowRoot`'s children can be counted too — a `ShadowRoot` is not an
 * `Element` but does expose `children`.
 */
function sameTypeIndexAmong(el: Element, siblings: HTMLCollection): number {
  let count = 0;
  for (let i = 0; i < siblings.length; i++) {
    const sibling = siblings[i];
    if (sibling.tagName === el.tagName) {
      count += 1;
      if (sibling === el) {
        return count;
      }
    }
  }
  // Defensive: element not found among its parent's children (shouldn't happen
  // for a live element). Return 1 rather than throwing.
  return 1;
}
